<template>
  <div ref="chartContainer" style="height: 350px;"></div>
</template>

<script>
import Highcharts from "highcharts";
import mouseWheelZoom from "highcharts/modules/mouse-wheel-zoom";

// CJS builds export a factory; ESM builds self-register on import.
if (typeof mouseWheelZoom === "function") mouseWheelZoom(Highcharts);

const FONT_FAMILY =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export default {
  name: "InputHealthChart",
  props: {
    chartData: {
      type: Object,
      default: () => ({})
    },
    label: {
      type: String,
      default: 'Value'
    },
  },
  data() {
    return {
      chart: null,
    }
  },
  mounted() {
    this.initChart();
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
  methods: {
    hasMultipleSeries() {
      return this.chartData?.datasets?.length > 1 && this.chartData.datasets[1]?.data?.length;
    },
    // Datasets carrying an explicit `name` opt into multi-metric mode (own
    // color, own y-axis via `yAxisIndex`, toggleable legend, optional
    // anomaly `zones` and `gapMs` line-breaking). Datasets without `name`
    // keep the legacy single/High-Low behavior.
    hasNamedSeries() {
      return this.chartData?.datasets?.some(d => d.name);
    },
    hasDualAxes() {
      return this.hasNamedSeries() && this.chartData.datasets.some(d => d.yAxisIndex === 1);
    },
    // Convert {x,y} points to [ms, y] pairs, inserting a null point whenever
    // two samples are further apart than `gapMs` — so encoder dropouts render
    // as a visible break instead of a line that pretends data existed.
    toPairs(data, gapMs) {
      const pairs = [];
      let prev = null;
      (data || []).forEach(p => {
        const time = new Date(p.x).getTime();
        if (gapMs && prev !== null && time - prev > gapMs) {
          pairs.push([prev + 1, null]);
        }
        pairs.push([time, p.y]);
        prev = time;
      });
      return pairs;
    },
    buildSeries() {
      if (this.hasNamedSeries()) {
        return this.chartData.datasets.map(d => ({
          name: d.name,
          data: this.toPairs(d.data, d.gapMs),
          color: d.borderColor || 'dodgerblue',
          yAxis: d.yAxisIndex || 0,
          showInLegend: this.chartData.datasets.length > 1,
          ...(d.dashStyle ? { dashStyle: d.dashStyle } : {}),
          ...(typeof d.lineWidth === 'number' ? { lineWidth: d.lineWidth } : {}),
          ...(d.zones ? { zones: d.zones, zoneAxis: 'y' } : {}),
        }));
      }

      const series = [];
      if (this.chartData?.datasets?.[0]) {
        const first = this.chartData.datasets[0];
        series.push({
          name: this.hasMultipleSeries() ? 'High' : this.label,
          data: this.toPairs(first.data, first.gapMs),
          color: 'dodgerblue',
          showInLegend: this.hasMultipleSeries(),
        });
      }
      if (this.chartData?.datasets?.[1]?.data?.length) {
        const second = this.chartData.datasets[1];
        series.push({
          name: 'Low',
          data: this.toPairs(second.data, second.gapMs),
          color: 'red',
          showInLegend: true,
        });
      }
      return series;
    },
    buildYAxis() {
      const labelStyle = {
        color: '#8794B5',
        fontSize: '12px',
        fontFamily: FONT_FAMILY,
      };
      const baseAxis = {
        min: 0,
        title: { text: null },
        gridLineColor: 'rgba(167, 181, 226, 0.08)',
        labels: { style: labelStyle },
      };

      if (!this.hasDualAxes()) return baseAxis;

      // Drop the rotated axis titles (the KPI header + legend already name the
      // series) and instead tint each axis's numeric labels to its series
      // color, so left / right stays clear while the plot widens. Colors come
      // from the first dataset assigned to each axis (handles 2 or 4 series).
      const datasets = this.chartData.datasets;
      const leftDs = datasets.find(d => (d.yAxisIndex || 0) === 0);
      const rightDs = datasets.find(d => d.yAxisIndex === 1);
      return [
        {
          ...baseAxis,
          ...(leftDs?.yAxisMax ? { max: leftDs.yAxisMax } : {}),
          title: { text: null },
          labels: { style: { ...labelStyle, color: leftDs?.borderColor || labelStyle.color } },
        },
        {
          ...baseAxis,
          ...(rightDs?.yAxisMax ? { max: rightDs.yAxisMax } : {}),
          gridLineWidth: 0,
          opposite: true,
          title: { text: null },
          labels: { style: { ...labelStyle, color: rightDs?.borderColor || labelStyle.color } },
        },
      ];
    },
    initChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      this.chart = new Highcharts.Chart(this.$refs.chartContainer, {
        chart: {
          type: 'line',
          backgroundColor: null,
          zooming: {
            type: 'x',
            // Wheel zoom pairs with the page-level handler that only lets
            // Ctrl/Cmd + scroll reach the chart.
            mouseWheel: { enabled: true, type: 'x' },
            resetButton: {
              position: { align: 'right', x: -4, y: 4 },
              theme: {
                fill: '#232D4D',
                stroke: '#3A456B',
                r: 6,
                style: {
                  color: '#C8D2F0',
                  fontSize: '12px',
                  fontFamily: FONT_FAMILY,
                },
                states: {
                  hover: {
                    fill: '#2C3860',
                    style: { color: '#fff' },
                  },
                },
              },
            },
          },
          selectionMarkerFill: 'rgba(45, 104, 255, 0.12)',
        },
        credits: { enabled: false },
        title: { text: null },
        legend: {
          enabled: true,
          align: 'left',
          itemStyle: {
            color: '#8794B5',
            fontFamily: FONT_FAMILY,
            fontSize: '12px',
          },
          itemHoverStyle: {
            color: '#fff',
          },
          itemHiddenStyle: {
            color: '#4D5677',
            textDecoration: 'line-through',
          },
        },
        xAxis: {
          type: 'datetime',
          lineColor: 'transparent',
          tickColor: 'transparent',
          gridLineWidth: 0,
          crosshair: {
            width: 1,
            color: 'rgba(167, 181, 226, 0.35)',
            dashStyle: 'ShortDash',
          },
          labels: {
            style: {
              color: '#8794B5',
              fontSize: '12px',
              fontFamily: FONT_FAMILY,
            },
          },
        },
        yAxis: this.buildYAxis(),
        tooltip: {
          shared: true,
          backgroundColor: 'rgba(10, 16, 34, 0.92)',
          borderColor: '#3A456B',
          borderRadius: 8,
          style: { color: '#fff', fontFamily: FONT_FAMILY },
          xDateFormat: '%d %b %Y %H:%M:%S',
        },
        plotOptions: {
          line: {
            marker: { enabled: false },
            lineWidth: 2,
            connectNulls: false,
          },
          series: {
            // Ease value changes on each live refresh instead of snapping.
            animation: { duration: 600 },
            states: {
              inactive: { opacity: 0.35 },
            },
          },
        },
        series: this.buildSeries(),
      });
    },
  },
  watch: {
    chartData: {
      handler() {
        if (!this.chart) {
          this.initChart();
          return;
        }

        // Recreate the chart if the axis layout changed (single <-> dual).
        const wantsDual = this.hasDualAxes();
        const hasDual = this.chart.yAxis.length > 1;
        if (wantsDual !== hasDual) {
          this.initChart();
          return;
        }

        const newSeries = this.buildSeries();

        // Animating a full redraw of hundreds of points every few seconds is
        // CPU-heavy, so only animate small windows (5m/15m); large windows
        // (e.g. 1h) update instantly.
        const maxPoints = newSeries.reduce((m, s) => Math.max(m, s.data.length), 0);
        const animate = maxPoints <= 200;

        // Update existing series or add new ones
        while (this.chart.series.length > newSeries.length) {
          this.chart.series[this.chart.series.length - 1].remove(false);
        }

        newSeries.forEach((s, i) => {
          const existing = this.chart.series[i];
          if (existing) {
            // Morph points in place (updatePoints=true) only when the count is
            // unchanged — the typical live tick. On a window/preset switch the
            // count changes a lot, so a plain replace avoids an expensive
            // point-by-point diff that briefly freezes the UI.
            const currentLen = existing.data ? existing.data.length : 0;
            const sameLength = currentLen === s.data.length;
            existing.setData(s.data, false, false, sameLength && animate);
            // Keep anomaly thresholds in sync with the rolling window.
            if (s.zones) {
              existing.update({ zones: s.zones, zoneAxis: 'y' }, false);
            }
          } else {
            this.chart.addSeries(s, false);
          }
        });

        // Keep axis headroom in sync with live data.
        if (wantsDual) {
          this.chartData.datasets.forEach((d) => {
            const axis = this.chart.yAxis[d.yAxisIndex || 0];
            if (d.yAxisMax && axis) {
              axis.update({ max: d.yAxisMax }, false);
            }
          });
        }

        // Animate the redraw so the axis extension and point moves glide.
        // easeOutQuart as a function avoids depending on named easings.
        this.chart.redraw(
          animate ? { duration: 600, easing: (pos) => 1 - Math.pow(1 - pos, 4) } : false
        );
      },
      // Shallow: the parent passes a fresh chartData object on every refresh,
      // so a reference watch fires without deep-diffing large point arrays.
    }
  }
};
</script>
